import { NavItem } from "@type/navItems";
import { MinimalSidebarV2, ProductToggleConfig } from "@component/mini-sidebar_v2";
import AdminSidebar from "@component/admin-sidebar";
import { Header } from "@component/header";
import MaintenanceBanner from "@container/maintenance-banner";
import { useUserProfileContext } from "@context/user_profile";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getCookie, setCookie } from "@util/cookie";
import { cn } from "@util/tailwind";
import NotificationDot from "@component/notification-dot";
import TrialUsageBanner from "@component/trial-usage-banner";
import { LAYOUT_TOP_SLOT_ID } from "@util/layoutSlots";

const ADMIN_ROLES = ["admin", "super_admin"];

/**
 * Routes that ask for the sidebar out of the way.
 *
 * A business drill-in is the densest screen the platform has — eight tabs of
 * icon and label measure 907px, and beside a 256px sidebar that is wider than
 * the content column on anything under about 1300px. Folding the rail back
 * gives the page those 176px, which is the difference between the tab strip
 * fitting and the tab strip scrolling.
 *
 * A route living in the platform layout is a leak, and a small one: the width
 * is decided here and nowhere else, so the alternative is threading a prop
 * through two shells that know nothing about Oura.
 */
function wantsCollapsedSidebar(pathname: string): boolean {
    return /^\/oura\/businesses\/[^/]+/.test(pathname);
}

interface Props {
    children: React.ReactNode;
    navItems: NavItem[];
    productToggle?: ProductToggleConfig;
};

export default function LayoutWithMinimalSidebarV2(props: Props) {
    const isAccountStartupScreening: boolean = Boolean((getCookie("__account_startup_screening__")));
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const [isShowDotResume, setShowDotResume] = useState<boolean>(!getCookie("__dot_resume_menu__"))
    const navigate = useNavigate();
    const currentPath = window.location.pathname;
    const currentPage = props.navItems.find((item) => item.to === currentPath)?.name;

    // Admin / super-admin get the labelled collapsible sidebar; everyone else
    // keeps the icon rail. The collapse state is persisted so it survives reloads.
    const isAdmin = ADMIN_ROLES.includes(currentUserInfo?.role || "");
    const [sidebarMini, setSidebarMini] = useState<boolean>(getCookie("__admin_sidebar_mini__") === "true");

    /**
     * The route folds the sidebar; the reader can unfold it and be obeyed.
     *
     * `manualOverride` is what they said while they are in there, and it is
     * deliberately NOT written to the cookie: the cookie is their standing
     * preference for the rest of the platform, and a route should not get to
     * rewrite it on their behalf. Leaving the drill-in drops the override and
     * their own preference takes over again.
     */
    const collapseForRoute = wantsCollapsedSidebar(useLocation().pathname);
    const [manualOverride, setManualOverride] = useState<boolean | null>(null);
    useEffect(() => { setManualOverride(null); }, [collapseForRoute]);

    const effectiveMini = manualOverride ?? (collapseForRoute ? true : sidebarMini);
    const toggleSidebar = () => {
        const next = !effectiveMini;
        if (collapseForRoute) { setManualOverride(next); return; }
        setSidebarMini(next);
        setCookie("__admin_sidebar_mini__", String(next));
    };

    return (
        <>
            <div className="bg-white dark:bg-gray-950">
                {isAdmin ? (
                    <AdminSidebar
                        navItems={props.navItems}
                        mini={effectiveMini}
                        onToggleMini={toggleSidebar}
                    />
                ) : (
                    <MinimalSidebarV2
                        navItems={props.navItems}
                        productToggle={props.productToggle}
                    />
                )}
                <div className={cn("ml-0", isAdmin && !effectiveMini ? "sm:ml-64" : "sm:ml-20")}>
                    <Header />
                    <MaintenanceBanner />
                    <TrialUsageBanner />
                    {/* A bar a screen portals in (Oura's What's new), in this column, never under the sidebar. */}
                    <div id={LAYOUT_TOP_SLOT_ID} className="empty:hidden" />
                    <main className="pt-4 sm:pt-6 pb-24 sm:pb-6 h-full">
                        {props.children}

                        <footer className="fixed bottom-0 left-0 w-screen border-t bg-white py-2 px-1 sm:hidden h-[71px]">
                            <div className="flex items-center justify-around space-x-2">
                                {props.navItems.map((item, index) => {
                                    if (!currentUserInfo || item.roles.indexOf(currentUserInfo.role || "") === -1) return;
                                    if (isAccountStartupScreening && ["jobs", "hires", "assessments"].includes(item.name)) return;
                                    // hide payment menu for hdbank referral code
                                    if (item.name === "payment" && import.meta.env.VITE_HDBANK_REFERRAL_CODE.split(",").includes(currentUserInfo.referral_code)) return;
                                    if (item.name === "hires" && currentUserInfo.permission === "member") return;

                                    const isActive = currentPage === item.name;
                                    const Icon = item.icon;
                                    return (
                                        <button key={index} className="relative flex-1 group flex aspect-square w-[54px] h-[54px] cursor-pointer flex-col items-center justify-center gap-1 text-black" onClick={() => {
                                            navigate(item.to as string)
                                            if (item.label === "Resume") {
                                                setShowDotResume(false);
                                                setCookie("__dot_resume_menu__", "true");
                                            }
                                        }}>

                                            <div className="rounded-xl text-gray-900 transition-all duration-300">
                                                <Icon className={`hover:scale-110 transition-all duration-300 h-7 w-7 shrink-0 mx-auto ${isActive ? "text-blue-600 hover:text-blue-600 transition-all duration-300" : ""}`} />
                                            </div>
                                            <span className={`text-[10px] ${isActive ? "text-blue-600" : ""}`}>{item.label}</span>
                                            {(isShowDotResume && item.label === "Resume") && (
                                                <div className="absolute -top-[8px] -right-1">
                                                    <NotificationDot size="small" />
                                                </div>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        </footer>
                    </main>
                </div>
            </div>
        </>
    );
}
